import { Suspense, useRef } from 'react';
import { PageSkeleton } from '@/components/shared/PageSkeleton';
import { ChunkErrorBoundary } from '@/components/shared/ChunkErrorBoundary';
import { useTabs } from '@/contexts/TabsContext';
import { findRoute } from '@/routes/routeMap';

/**
 * Renders every open tab's page simultaneously, hiding all but the active one
 * with CSS `display: none`. Keeps React state, scroll position, and data
 * alive when the user switches tabs (iOS-tab-bar style).
 */
export const TabbedContent = () => {
  const { tabs, activeId } = useTabs();
  // a tab's page is mounted the first time it is opened, then kept alive
  const seen = useRef(new Set());
  seen.current.add(activeId);

  return (
    <div className="tabbed-content" data-testid="tabbed-content">
      {tabs.map((tab) => {
        const route = findRoute(tab.path);
        const Page = route ? route.element : null;
        const visible = tab.id === activeId;
        return (
          <div
            key={tab.id}
            style={{ display: visible ? 'block' : 'none' }}
            aria-hidden={!visible}
            data-testid={`tab-panel-${tab.id}`}
          >
            {!seen.current.has(tab.id) ? null : Page ? (
              <ChunkErrorBoundary>
                <Suspense fallback={<PageSkeleton />}>
                  {/* refreshKey forces a remount on manual refresh */}
                  <Page key={tab.refreshKey || 0} />
                </Suspense>
              </ChunkErrorBoundary>
            ) : (
              <div className="p-8 text-center text-slate-500">
                Page not found: <code className="font-mono">{tab.path}</code>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
